import { GridFilterItem } from '@mui/x-data-grid-pro';

import { HttpMethod, httpClient } from './http-client';
import {
  PartExistance,
  NSPartExistance,
  BulkUpdatePartDataset,
  PartHistoryMovementListFilterOptions,
  UpdatePartLocationObject,
  PartObject,
  PartConditionDropdown,
  PartLocationObject,
  PartHistoryDataType,
  PartHistoryMovementListResponse,
  DuplicatePartsDataType,
  PartHistory,
} from '../@types/part.type';
import { PartActionHistoryDataType } from '../@types/partHistory.type';
import { WarrantyPanalType } from '../@types/warrantySummary.type';
import { DuplicatePartsPanelProps } from '../components/molecules/DuplicatePartsPanel/DuplicatePartsPanel.props';
import { PartTypeLocationItemData } from '../components/molecules/PartTypeLocationItem';

// Response type for part update mutations
interface PartUpdateResponse {
  id: number;
  [key: string]: unknown;
}

// Response type for bulk update file processing
interface BulkUpdateFileResponse {
  success: boolean;
  processedCount?: number;
  errors?: Array<{ row: number; message: string }>;
}

// Response type for existing serials check
interface ExistingSerialsResponse {
  serials: Array<{ serial1: string; exists: boolean }>;
}

export const getPartById = (id: number): Promise<PartObject> => {
  return httpClient<PartObject>(`/parts/${id}`, HttpMethod.Get);
};

export const getPartByIdForService = (id: number): Promise<PartObject> => {
  return httpClient<PartObject>(`/parts/${id}/forService`, HttpMethod.Get);
};

export const getPartConditions = (): Promise<PartConditionDropdown[]> => {
  return httpClient<PartConditionDropdown[]>('/partConditions', HttpMethod.Get);
};

export const getPartConditionsForService = (): Promise<PartConditionDropdown[]> => {
  return httpClient<PartConditionDropdown[]>('/partConditionsForService', HttpMethod.Get);
};

export const updatePart = (id: number, part: Partial<PartObject>): Promise<PartUpdateResponse> => {
  return httpClient<PartUpdateResponse>(`/parts/${id}`, HttpMethod.Put, part);
};

export const updatePartForService = (id: number, part: Partial<PartObject>): Promise<PartUpdateResponse> => {
  return httpClient<PartUpdateResponse>(`/parts/${id}/forService`, HttpMethod.Put, part);
};

export const getAllWarrantySummary = (partId: number): Promise<WarrantyPanalType[]> => {
  return httpClient<WarrantyPanalType[]>(`/warranties/getByPart/${partId}`, HttpMethod.Get);
};

export const getPartLocationDataById = (id: number): Promise<PartLocationObject> => {
  return httpClient<PartLocationObject>(`/parts/locationData/${id}`, HttpMethod.Get);
};

export const getPartLocationDataByPartId = (id: number, locationData: UpdatePartLocationObject): Promise<PartLocationObject> => {
  const { locationId, locationTypeCode, locationHierarchy } = locationData;

  return httpClient<PartLocationObject>(`/parts/locationData/${id}/updatePart?locationId=${locationId}&locationTypeCode=${encodeURIComponent(locationTypeCode)}&locationHierarchy=${encodeURIComponent(locationHierarchy)}`, HttpMethod.Get);
};

export const getPartLocationDataByIdForService = (id: number): Promise<PartLocationObject> => {
  return httpClient<PartLocationObject>(`/parts/locationData/${id}/forService`, HttpMethod.Get);
};

export const getPartLocationDataByServiceId = (partTypeId: number, serviceId: number): Promise<PartTypeLocationItemData[]> => {
  return httpClient<PartTypeLocationItemData[]>(`/parts/${partTypeId}/byServiceId/${serviceId}`, HttpMethod.Get);
};

export const updatePartLocation = (id: number, part: Partial<PartLocationObject>): Promise<PartUpdateResponse> => {
  return httpClient<PartUpdateResponse>(`/parts/${id}/location`, HttpMethod.Put, part);
};

export const getPartHistoryByPartId = (id: number): Promise<PartHistoryDataType[]> => {
  return httpClient<PartHistoryDataType[]>(`/parts/${id}/history`, HttpMethod.Get);
};

export const getPartExistanceBySerials = (data: PartExistance): Promise<PartExistance[]> => {
  return httpClient<PartExistance[]>('/bulkSearchParts', HttpMethod.Post, data);
};

export const getNSPartExistanceBySerials = (data: NSPartExistance): Promise<NSPartExistance[]> => {
  return httpClient<NSPartExistance[]>('/checkNSPart', HttpMethod.Post, data);
};

export const updateBulkPart = (data: BulkUpdatePartDataset): Promise<PartUpdateResponse> => {
  return httpClient<PartUpdateResponse>('/bulkUpdateParts', HttpMethod.Put, data);
};

export const getPartMovementHistory = (queryOptions: PartHistoryMovementListFilterOptions): Promise<PartHistoryMovementListResponse> => {
  const { filterModel, page, pageSize, sortModel, startDate, endDate, partId } = queryOptions;
  let url = `/partMovementHistory?limit=${pageSize}&page=${page + 1}&startDate=${startDate}&endDate=${endDate}`;

  if (partId) {
    url += `&partId=${partId}`;
  }

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = filter?.operator;
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });

  return httpClient<PartHistoryMovementListResponse>(url, HttpMethod.Get);
};

export const getPartRepairHistory = (partId: number): Promise<PartHistory[]> => {
  return httpClient<PartHistory[]>(`/partHistory/${partId}/repairHistory`, HttpMethod.Get);
};

export const getLatestPartMomentHistory = (partId: number): Promise<PartHistory> => {
  const url = `/partHistory/latestPartMovement/${partId}`;
  return httpClient<PartHistory>(url, HttpMethod.Get);
};

export const getPartHistoryByServiceIdAndHistoryTypeCodes = (serviceId: number, historyTypes: string[]): Promise<PartActionHistoryDataType[]> => {
  return httpClient<PartActionHistoryDataType[]>(`/partHistory/services/${serviceId}?historyTypes=${JSON.stringify(historyTypes)}`, HttpMethod.Get);
};

export const getDuplicatePartsBySerial1AndPartType = (mainPartId: number, serial1: string, partTypeId: number): Promise<DuplicatePartsDataType[]> => {
  return httpClient<DuplicatePartsDataType[]>(`/getDuplicateParts/?mainPartId=${mainPartId}&serial1=${serial1}&partTypeId=${partTypeId}`, HttpMethod.Get);
};

export const mergeDuplicateParts = (data: DuplicatePartsPanelProps): Promise<PartUpdateResponse> => {
  return httpClient<PartUpdateResponse>('/mergeDuplicateParts', HttpMethod.Post, data);
};

export const createExceptionalPart = (part: Partial<PartObject>): Promise<PartObject> => {
  return httpClient<PartObject>('/parts/exception', HttpMethod.Post, part);
};

export const updateExceptionalPart = (id: number, data: Partial<PartObject>): Promise<PartObject> => {
  return httpClient<PartObject>(`/parts/${id}/exception`, HttpMethod.Put, data);
};

export const processPartBulkUpdateFile = (fileList: FormData): Promise<BulkUpdateFileResponse> => {
  const headers = { 'Content-Type': 'multipart/form-data' };

  return httpClient<BulkUpdateFileResponse>('/parts/bulkUpdate/fileProcess', HttpMethod.Post, fileList, headers);
};

export const getExistingSerials = (data: { serials: string[] }): Promise<ExistingSerialsResponse> => {
  return httpClient<ExistingSerialsResponse>('/parts/getExistingSerials', HttpMethod.Post, data);
};